import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import logo from "../assets/logo-full.png";
import frFlag from "../assets/fr-flag.png";
import ukFlag from "../assets/uk-flag.png";

const Container = styled(Box)({
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "0 24px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const NavLink = styled(Button)({
  fontWeight: "bold",
  color: "#449F2D",
  textTransform: "none",
  fontSize: "1rem",
  backgroundColor: "transparent",
  "&:hover": {
    textDecoration: "underline",
    backgroundColor: "transparent",
  },
});

const CtaButton = styled(Button)({
  fontWeight: "bold",
  color: "#fff",
  backgroundColor: "#449F2D",
  borderRadius: "999px",
  textTransform: "none",
  padding: "8px 20px",
  whiteSpace: "nowrap",
  fontSize: "0.90rem",
  transition: "transform 0.2s ease, background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#388624",
    transform: "scale(1.07)",
  },
});

const flagIcons = {
  fr: frFlag,
  en: ukFlag,
};

const Navbar = () => {
  const { t } = useTranslation("Navbar");
  const isMobile = useMediaQuery("(max-width:1030px)");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [language, setLanguage] = useState(i18n.language || "fr");
  const navigate = useNavigate();

  const toggleDrawer = (open) => () => setDrawerOpen(open);

  const navItems = [
    { label: t("guarantees"), path: "/garanties" },
    { label: t("partners"), path: "/partenaires" },
    { label: t("about"), path: "/qui-sommes-nous" },
  ];

  const renderFlagSelect = (isMobile = false) => (
    <Select
      value={language}
      onChange={(event) => {
        const newLanguage = event.target.value;
        setLanguage(newLanguage);
        i18n.changeLanguage(newLanguage);
      }}
      variant="standard"
      disableUnderline
      sx={{
        display: "flex",
        alignItems: "center",
        fontWeight: "bold",
        color: "#449F2D",
        fontSize: "1rem",
        minWidth: 80,
        mt: isMobile ? 2 : 0,
        "& .MuiSelect-icon": {
          color: "#449F2D",
          right: 6,
        },
      }}
      renderValue={(value) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            component="img"
            src={flagIcons[value]}
            alt={value}
            sx={{
              width: 26,
              height: 18,
              objectFit: "cover",
            }}
          />

          <span>{value.toUpperCase()}</span>
        </Box>
      )}
    >
      <MenuItem value="fr">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img src={frFlag} alt="FR" width="20" height="14" />
          Français
        </Box>
      </MenuItem>

      <MenuItem value="en">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img src={ukFlag} alt="EN" width="20" height="14" />
          English
        </Box>
      </MenuItem>
    </Select>
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: "#fff",
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
        borderBottom: "1px solid #e0e0e0",
        zIndex: 1100,
      }}
    >
      <Toolbar disableGutters sx={{ minHeight: 90 }}>
        <Container>
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              height: 70,
              cursor: "pointer",
            }}
            onClick={() => navigate("/garanties")}
          />

          {isMobile ? (
            <>
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={toggleDrawer(true)}
              >
                <MenuIcon sx={{ color: "#449F2D" }} />
              </IconButton>

              <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
              >
                <Box
                  sx={{
                    width: 250,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {navItems.map((item) => (
                    <List key={item.label}>
                      <ListItem
                        button
                        onClick={() => {
                          toggleDrawer(false)();
                          navigate(item.path);
                        }}
                      >
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontWeight: "bold",
                            color: "#449F2D",
                          }}
                        />
                      </ListItem>
                    </List>
                  ))}

                  <CtaButton
                    fullWidth
                    onClick={() => {
                      toggleDrawer(false)();
                      navigate("/parcours-client");
                    }}
                  >
                    {t("protect")}
                  </CtaButton>

                  {renderFlagSelect(true)}
                </Box>
              </Drawer>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                gap: 5,
                alignItems: "center",
              }}
            >
              {navItems.map((item) => (
                <NavLink
                  key={item.label}
                  onClick={() => navigate(item.path)}
                >
                  {item.label}
                </NavLink>
              ))}

              <CtaButton onClick={() => navigate("/parcours-client")}>
                {t("protect")}
              </CtaButton>

              {renderFlagSelect()}
            </Box>
          )}
        </Container>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;